import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import AjustementTable from './AjustementTable';
import AVMEstimate from './AVMEstimate';
import AVMProEstimate from './AVMProEstimate';
import { eurosToFrench } from '@/utils/numberToFrench';
import {
  METHODE_VALORISATION_OPTIONS,
  LIQUIDITE_VALORISATION_OPTIONS,
  DELAI_COMMERCIALISATION_OPTIONS,
  REGIME_FISCAL_OPTIONS,
} from '@/constants/enums';



export default function SectionValorisation({ valorisation, expertise, typeBien, onChangeValo, onChangeExpertise, onRefresh }) {
  const valoVal = (field) => valorisation?.[field] ?? '';
  const expVal = (field) => expertise?.[field] ?? '';

  const handleValo = (field) => (e) => onChangeValo(field, e.target.value);
  const handleValoNumber = (field) => (e) => onChangeValo(field, e.target.value === '' ? '' : Number(e.target.value));
  const handleExp = (field) => (e) => onChangeExpertise(field, e.target.value);
  const handleExpNumber = (field) => (e) => {
    const val = e.target.value === '' ? '' : Number(e.target.value);
    onChangeExpertise(field, val);
    if (field === 'valeur_retenue_euros') {
      onChangeExpertise('valeur_retenue_lettres', eurosToFrench(val));
    }
  };

  const handleAVMPrefill = (values) => {
    if (values.valeur_unitaire_m2 != null) {
      onChangeValo('comparaison_valeur_unitaire_m2', values.valeur_unitaire_m2);
    }
    if (values.valeur_totale != null) {
      onChangeValo('comparaison_valeur_totale', values.valeur_totale);
    }
  };

  return (
    <div className="space-y-8">
      {/* Estimation AVM — Carteron (résidentiel) OU Carex Pro (commercial) selon type_bien */}
      <AVMEstimate
        expertiseId={expertise?.id}
        typeBien={typeBien}
        onPrefillComparaison={handleAVMPrefill}
      />
      <AVMProEstimate
        expertiseId={expertise?.id}
        typeBien={typeBien}
        onPrefillComparaison={handleAVMPrefill}
      />

      {/* Methode par comparaison */}
      <section>
        <h3 className="text-lg font-semibold text-slate-900 mb-4">Méthode par comparaison</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Input
            label="Valeur unitaire (€/m²)"
            name="comparaison_valeur_unitaire_m2"
            type="number"
            value={valoVal('comparaison_valeur_unitaire_m2')}
            onChange={handleValoNumber('comparaison_valeur_unitaire_m2')}
          />
          <Input
            label="Valeur totale (€)"
            name="comparaison_valeur_totale"
            type="number"
            value={valoVal('comparaison_valeur_totale')}
            onChange={handleValoNumber('comparaison_valeur_totale')}
          />
        </div>
        <div className="mt-4">
          <Input
            label="Commentaire"
            name="comparaison_commentaire"
            type="textarea"
            value={valoVal('comparaison_commentaire')}
            onChange={handleValo('comparaison_commentaire')}
          />
        </div>
      </section>

      {/* Methode par capitalisation */}
      <section>
        <h3 className="text-lg font-semibold text-slate-900 mb-4">Méthode par capitalisation</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Input
            label="Loyer annuel (€)"
            name="capitalisation_loyer_annuel"
            type="number"
            value={valoVal('capitalisation_loyer_annuel')}
            onChange={handleValoNumber('capitalisation_loyer_annuel')}
          />
          <Input
            label="Taux de capitalisation (%)"
            name="capitalisation_taux"
            type="number"
            value={valoVal('capitalisation_taux')}
            onChange={handleValoNumber('capitalisation_taux')}
          />
          <Input
            label="Valeur totale (€)"
            name="capitalisation_valeur_totale"
            type="number"
            value={valoVal('capitalisation_valeur_totale')}
            onChange={handleValoNumber('capitalisation_valeur_totale')}
          />
        </div>
        <div className="mt-4">
          <Input
            label="Commentaire"
            name="capitalisation_commentaire"
            type="textarea"
            value={valoVal('capitalisation_commentaire')}
            onChange={handleValo('capitalisation_commentaire')}
          />
        </div>
      </section>

      {/* Methode sol + construction */}
      <section>
        <h3 className="text-lg font-semibold text-slate-900 mb-4">Méthode sol + construction</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Input
            label="Valeur du terrain (€)"
            name="sol_construction_valeur_terrain"
            type="number"
            value={valoVal('sol_construction_valeur_terrain')}
            onChange={handleValoNumber('sol_construction_valeur_terrain')}
          />
          <Input
            label="Coût de construction à neuf (€)"
            name="sol_construction_cout_neuf"
            type="number"
            value={valoVal('sol_construction_cout_neuf')}
            onChange={handleValoNumber('sol_construction_cout_neuf')}
          />
          <Input
            label="Vétusté (%)"
            name="sol_construction_vetuste_pct"
            type="number"
            value={valoVal('sol_construction_vetuste_pct')}
            onChange={handleValoNumber('sol_construction_vetuste_pct')}
          />
          <Input
            label="Valeur totale (€)"
            name="sol_construction_valeur_totale"
            type="number"
            value={valoVal('sol_construction_valeur_totale')}
            onChange={handleValoNumber('sol_construction_valeur_totale')}
          />
        </div>
        <div className="mt-4">
          <Input
            label="Commentaire"
            name="sol_construction_commentaire"
            type="textarea"
            value={valoVal('sol_construction_commentaire')}
            onChange={handleValo('sol_construction_commentaire')}
          />
        </div>
      </section>

      {/* Synthese */}
      <section>
        <h3 className="text-lg font-semibold text-slate-900 mb-4">Synthèse</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Select
            label="Méthode retenue"
            name="methode_retenue"
            value={valoVal('methode_retenue')}
            options={METHODE_VALORISATION_OPTIONS}
            onChange={handleValo('methode_retenue')}
          />
          <Input
            label="Valeur avant ajustements (€)"
            name="valeur_avant_ajustements"
            type="number"
            value={valoVal('valeur_avant_ajustements')}
            onChange={handleValoNumber('valeur_avant_ajustements')}
          />
          <Input
            label="Valeur après ajustements (€)"
            name="valeur_apres_ajustements"
            type="number"
            value={valoVal('valeur_apres_ajustements')}
            onChange={handleValoNumber('valeur_apres_ajustements')}
          />
        </div>
      </section>

      {/* Ajustements */}
      {valorisation?.id && (
        <AjustementTable
          ajustements={valorisation.ajustements || []}
          valorisationId={valorisation.id}
          onRefresh={onRefresh}
        />
      )}

      {/* Conclusion */}
      <section>
        <h3 className="text-lg font-semibold text-slate-900 mb-4">Conclusion</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Valeur retenue (€)"
            name="valeur_retenue_euros"
            type="number"
            value={expVal('valeur_retenue_euros')}
            onChange={handleExpNumber('valeur_retenue_euros')}
          />
          <Input
            label="Valeur en lettres"
            name="valeur_retenue_lettres"
            type="text"
            value={expVal('valeur_retenue_lettres')}
            onChange={handleExp('valeur_retenue_lettres')}
          />
          <Input
            label="Décote vente forcée (%)"
            name="decote_pourcentage"
            type="number"
            value={valoVal('decote_pourcentage')}
            onChange={handleValoNumber('decote_pourcentage')}
          />
          <Select
            label="Liquidité"
            name="liquidite"
            value={valoVal('liquidite')}
            onChange={handleValo('liquidite')}
            options={LIQUIDITE_VALORISATION_OPTIONS}
          />
          <Select
            label="Délai de commercialisation"
            name="delai_commercialisation"
            value={valoVal('delai_commercialisation')}
            onChange={handleValo('delai_commercialisation')}
            options={DELAI_COMMERCIALISATION_OPTIONS}
          />
          <Select
            label="Régime fiscal"
            name="regime_fiscal"
            value={valoVal('regime_fiscal')}
            onChange={handleValo('regime_fiscal')}
            options={REGIME_FISCAL_OPTIONS}
          />
          <Input
            label="Taux de fiscalité (%)"
            name="taux_fiscalite"
            type="number"
            value={valoVal('taux_fiscalite')}
            onChange={handleValoNumber('taux_fiscalite')}
          />
        </div>
      </section>

    </div>
  );
}
